import { forwardRef, HTMLAttributes, ReactNode } from 'react';

import { cn } from 'styles';

import { Card, CardProps } from './Card';

type WrapperProps = Pick<CardProps, 'variant'>;

interface TitledCardProps extends WrapperProps {
  className?: string;
  contentClassName?: string;

  title?: ReactNode;
  titleClassName?: string;

  description?: ReactNode;
  descriptionClassName?: string;

  children?: ReactNode;

  header?: ReactNode;

  spacing?: 'normal' | 'tight';
}

const spacingClasses = {
  header: {
    normal: 'p-6 pb-4',
    tight: 'p-4 pb-2',
  },

  content: {
    normal: 'p-6 pt-0',
    tight: 'p-4 pt-0',
  },
};

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);

CardTitle.displayName = 'CardTitle';

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <div className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function TitledCard({
  className,
  contentClassName,

  header,
  title,
  titleClassName,
  description,
  descriptionClassName,

  children,
  variant,
  spacing = 'normal',
}: TitledCardProps) {
  const addHeader = !!title || !!description;

  const defaultHeader = addHeader ? (
    <div className={`flex flex-col gap-1.5 ${spacingClasses.header[spacing]}`}>
      <CardTitle className={titleClassName}>{title}</CardTitle>

      <CardDescription className={descriptionClassName}>{description}</CardDescription>
    </div>
  ) : null;

  return (
    <Card className={className} variant={variant} spacing="none">
      {header ?? defaultHeader}

      <div className={cn(spacingClasses.content[spacing], contentClassName)}>{children}</div>
    </Card>
  );
}
